/**
 * N-slot remote media: builds MediaStreams from mediasoup consumers and hands them
 * to the host app. Framework-free AND DOM-free — the module never touches `<video>`
 * elements, overlays or `document`. It only creates/stores streams and exposes them
 * so the client can render them however it wants.
 *
 * Rendering concerns that read the DOM (FPS counting, stall detection, tab-focus
 * resync, overlay pause/play) are NOT here — they live in the host app, which
 * subscribes via `onSlotStream` and drives `requestKeyframes`/`restoreSlot` back
 * into this module.
 *
 * This is the core slot/stream lifecycle only — two related concerns live in their own
 * files and are wired in below:
 *   - `rtp-stats.ts` — bitrate/dropped-frame polling and the `onSlotMetrics` stream.
 *   - `slot-restore-schedule.ts` — the "keep retrying a keyframe request" scheduler.
 */

import type { types as Mediasoup } from "mediasoup-client";

import { STATUS_CODES } from "../status-codes";
import type { StatusCodeValue } from "../status-codes";
import type { ProtooClient } from "../signaling/protoo-client";
import { createRtpStatsTracker, type SlotMetrics } from "./rtp-stats";
import { createSlotRestoreScheduler } from "./slot-restore-schedule";
import { createVideoLatencyControl } from "./video-latency-control";

export interface CreateDualVideoPipelineDeps {
  appendDiag: (s: string, opts?: { level?: string }) => void;
  protoo: Pick<ProtooClient, "notify">;
  slotCount?: number;
}

interface TrackSnapshot {
  id: string;
  readyState: MediaStreamTrackState;
  muted: boolean;
  enabled: boolean;
}

export type SlotStreamListener = (slot: number, stream: MediaStream | null) => void;

export function createDualVideoPipeline(deps: CreateDualVideoPipelineDeps) {
  const { appendDiag, protoo } = deps;

  let slotCount = Math.max(1, Math.floor(Number(deps.slotCount) || 2));

  const streams: (MediaStream | null)[] = [];
  const slotConsumers: (Mediasoup.Consumer | null)[] = [];
  const slotTrackState: (TrackSnapshot | null)[] = [];
  const slotStreamingEnabled: boolean[] = [];
  const streamListeners = new Set<SlotStreamListener>();
  // Replaces a `consumer.__opCloseLogged` property bolted onto the mediasoup Consumer at
  // runtime — Consumer is a real, closed class (no index signature), so that hack can't
  // type-check against it. A WeakSet has the same one-shot-per-consumer semantics and the
  // same lifetime (entries fall away with the consumer, nothing to clean up by hand).
  const loggedClosedConsumers = new WeakSet<Mediasoup.Consumer>();

  let recvMediaReady = false;

  function pushSlotDefaults() {
    streams.push(new MediaStream());
    slotConsumers.push(null);
    slotTrackState.push(null);
    slotStreamingEnabled.push(true);
  }

  for (let i = 0; i < slotCount; i += 1) pushSlotDefaults();

  /** Grow per-slot arrays so `slot` is in range. */
  function ensureSlots(needed: number) {
    const n = Math.max(0, Math.floor(Number(needed) || 0)) + 1;
    while (slotCount < n) {
      pushSlotDefaults();
      slotCount += 1;
    }
  }

  function forEachSlot(fn: (slot: number) => void) {
    for (let i = 0; i < slotCount; i += 1) fn(i);
  }

  function slotStateKey(slot: number): string {
    const c = slotConsumers[slot];
    const state = slotTrackState[slot];
    if (!slotStreamingEnabled[slot]) return "off";
    if (!c || c.closed || !state) return "idle";
    if (state.readyState === "ended") return "ended";
    if (state.muted) return "muted";
    return "live";
  }

  function requestKeyframes(slot: number) {
    const consumer = slotConsumers[slot];
    if (!consumer || consumer.closed) return;
    protoo.notify("resumeConsumer", { consumerId: consumer.id });
    void requestKeyframesForConsumer(consumer).catch(() => {});
  }

  const latencyControl = createVideoLatencyControl({
    getReceiver: (slot) => {
      const consumer = slotConsumers[slot];
      return consumer && !consumer.closed ? consumer.rtpReceiver : undefined;
    },
    // The controller publishes only on an action/timer, after both factories return.
    onUpdate: (slot) => {
      rtpStats.emitSlotMetrics(slot);
    },
  });
  const rtpStats = createRtpStatsTracker({
    slotCount: () => slotCount,
    getConsumer: (slot) => slotConsumers[slot] ?? null,
    getStateKey: slotStateKey,
    appendDiag,
    getLatencySnapshot: (slot) => latencyControl.snapshot(slot),
    onSample: (slot, sample) => latencyControl.record(slot, sample),
  });

  const restoreScheduler = createSlotRestoreScheduler({
    isEnabled: (slot) => Boolean(slotStreamingEnabled[slot]),
    isReady: () => recvMediaReady,
    onRestore: (slot) => requestKeyframes(slot),
  });

  let holdLastFrame = false;
  let releaseHoldTimer: ReturnType<typeof setTimeout> | null = null;

  function setHoldLastFrame(hold: boolean) {
    if (releaseHoldTimer) {
      clearTimeout(releaseHoldTimer);
      releaseHoldTimer = null;
    }
    holdLastFrame = Boolean(hold);
  }

  function scheduleReleaseHoldLastFrame(delayMs = 5000) {
    if (releaseHoldTimer) clearTimeout(releaseHoldTimer);
    releaseHoldTimer = setTimeout(() => {
      releaseHoldTimer = null;
      holdLastFrame = false;
    }, delayMs);
  }

  function getSlotStream(slot: number): MediaStream | null {
    return (slot >= 0 && slot < slotCount ? streams[slot] : undefined) ?? null;
  }

  function onSlotStream(fn: SlotStreamListener) {
    streamListeners.add(fn);
    // Replay current streams so a late subscriber (e.g. the host hook mounting after
    // session.start()) receives already-attached consumers instead of missing them.
    for (let slot = 0; slot < slotCount; slot += 1) {
      try {
        fn(slot, getSlotStream(slot));
      } catch {
        /* listener errors are isolated */
      }
    }
    return () => streamListeners.delete(fn);
  }

  function emitSlotStream(slot: number) {
    const stream = getSlotStream(slot);
    for (const fn of streamListeners) {
      try {
        fn(slot, stream);
      } catch {
        /* listener errors are isolated */
      }
    }
  }

  async function requestKeyframesForConsumer(consumer: Mediasoup.Consumer) {
    protoo.notify("resumeConsumer", { consumerId: consumer.id });
    for (let i = 0; i < 3; i += 1) {
      protoo.notify("requestConsumerKeyFrame", { consumerId: consumer.id });
      await new Promise((r) => setTimeout(r, 350));
    }
  }

  /**
   * Recv ICE/DTLS is up — first moment RTP can arrive. PLI before this is wasted.
   */
  function onRecvTransportReady() {
    recvMediaReady = true;
    appendDiag("recv ICE connected → request keyframes", { level: "debug" });
    forEachSlot((slot) => {
      if (!slotStreamingEnabled[slot]) return;
      requestKeyframes(slot);
    });
  }

  /**
   * Rebuild a fresh MediaStream from the slot's track and re-emit it. Same path that
   * fixes black video on tab focus / producer recreate. The host reassigns the new
   * stream to its `<video>`.
   */
  function restoreSlot(slot: number): boolean {
    const consumer = slotConsumers[slot];
    if (!consumer || consumer.closed) return false;
    if (!consumer.track) return false;
    const fresh = new MediaStream([consumer.track]);
    streams[slot] = fresh;
    emitSlotStream(slot);
    requestKeyframes(slot);
    return true;
  }

  function destroy() {
    holdLastFrame = false;
    recvMediaReady = false;
    if (releaseHoldTimer) {
      clearTimeout(releaseHoldTimer);
      releaseHoldTimer = null;
    }
    rtpStats.stopStatsPolling();
    latencyControl.destroy();
    restoreScheduler.invalidateSlotRestores();
    streamListeners.clear();
    forEachSlot((i) => {
      const c = slotConsumers[i];
      if (c) closeVideoConsumer(i, c, "session stop");
      streams[i]?.getVideoTracks().forEach((t) => {
        try {
          t.stop();
        } catch {
          /* ignore */
        }
      });
      emitSlotStream(i); // signal the client that the slot has no stream
    });
  }

  function logVideoConsumerClosed(slot: number, consumer: Mediasoup.Consumer, reason: string) {
    if (loggedClosedConsumers.has(consumer)) return;
    loggedClosedConsumers.add(consumer);
    const producer = consumer.producerId ?? "?";
    appendDiag(
      `video consumer closed slot=${slot + 1} id=${consumer.id} producer=${producer} (${reason})`
    );
  }

  function clearSlotConsumerState(slot: number, consumer: Mediasoup.Consumer, reason: string) {
    logVideoConsumerClosed(slot, consumer, reason);
    if (slotConsumers[slot]?.id === consumer.id) {
      slotConsumers[slot] = null;
      rtpStats.resetSlot(slot);
      slotTrackState[slot] = null;
      latencyControl.receiverChanged(slot);
      if (!holdLastFrame) {
        streams[slot]?.getVideoTracks().forEach((t) => {
          try {
            t.stop();
          } catch {
            /* ignore */
          }
        });
        streams[slot] = null;
        emitSlotStream(slot); // signal the client that the slot is now blank
      }
    }
  }

  function closeVideoConsumer(slot: number, consumer: Mediasoup.Consumer | null, reason: string) {
    if (!consumer) return;
    clearSlotConsumerState(slot, consumer, reason);
    if (!consumer.closed) {
      try {
        consumer.close();
      } catch {
        /* ignore */
      }
    }
  }

  /** Drop a slot consumer by id without requiring the mediasoup "close" event. */
  function dropConsumerById(consumerId: string, reason = "drop"): boolean {
    const id = String(consumerId);
    for (let slot = 0; slot < slotCount; slot += 1) {
      const c = slotConsumers[slot];
      if (!c || String(c.id) !== id) continue;
      clearSlotConsumerState(slot, c, reason);
      return true;
    }
    return false;
  }

  function disposePreviousConsumer(
    slot: number,
    keepId: string,
    hooks?: { beforeClose?: (prev: Mediasoup.Consumer) => void }
  ) {
    const prev = slotConsumers[slot];
    if (!prev || prev.id === keepId) return;
    hooks?.beforeClose?.(prev);
    closeVideoConsumer(slot, prev, "replaced");
  }

  function trackSnapshot(track: MediaStreamTrack): TrackSnapshot {
    return {
      id: track.id,
      readyState: track.readyState,
      muted: track.muted,
      enabled: track.enabled,
    };
  }

  async function attachVideoConsumer(
    consumer: Mediasoup.Consumer,
    slot: number,
    producerId: string,
    ui: { setStatus: (code: StatusCodeValue) => void },
    hooks?: { beforeReplace?: (prev: Mediasoup.Consumer) => void }
  ) {
    ensureSlots(slot);
    const track = consumer?.track;
    if (!track) {
      appendDiag(`attachVideoConsumer: missing track slot=${slot + 1}`, { level: "warn" });
      return;
    }

    // Dispose of any previous consumer on this slot BEFORE installing the new stream:
    // clearSlotConsumerState() stops tracks on the *current* streams[slot], so it must
    // run while that still refers to the OLD stream — otherwise it stops the brand new
    // track and nulls out the slot instead of the one being replaced.
    disposePreviousConsumer(slot, consumer.id, { beforeClose: hooks?.beforeReplace });

    // Always build a brand new MediaStream on track change rather than mutating the
    // tracks of an already-attached stream — swapping tracks in-place can leave the
    // video element stuck on the last frame after a producer/consumer replacement.
    streams[slot] = new MediaStream([track]);
    slotConsumers[slot] = consumer;
    slotTrackState[slot] = trackSnapshot(track);

    // "transportclose" (fires when the owning Transport is closed — e.g. teardown-phase.ts
    // during every rejoin/stop), NOT "close" — mediasoup-client's Consumer only emits a
    // plain "close" on its `.observer` sub-emitter, never on itself (verified against the
    // vendored bundle: Consumer.close() does `this.emit("@close")` (internal) and
    // `this._observer.safeEmit("close")`, nothing named "close" on the consumer itself).
    // Consumer.transportClosed() — what actually runs when the owning transport closes —
    // does emit "transportclose" on itself, which is the real, always-reachable trigger
    // for the teardown/rejoin case this handler exists for.
    consumer.on("transportclose", () => {
      clearSlotConsumerState(slot, consumer, "transportclose");
    });

    const sync = () => {
      const t = consumer.track;
      slotTrackState[slot] = trackSnapshot(t);
      rtpStats.publishRtpStats();
    };
    consumer.track.addEventListener("ended", () => {
      appendDiag(`track ended id=${consumer.track.id}`, { level: "warn" });
      sync();
    });
    (["mute", "unmute"] as const).forEach((ev) =>
      consumer.track.addEventListener(ev, () => {
        console.debug(`[diag] track ${ev} id=${consumer.track.id}`);
        sync();
      })
    );

    emitSlotStream(slot);

    ui.setStatus(STATUS_CODES.VIDEO_SLOT_ACTIVE);
    appendDiag(`video slot ${slot + 1} playing track=${track.id}`);

    if (holdLastFrame) {
      scheduleReleaseHoldLastFrame(2000);
    }

    // PLI only once RTP can flow. Before ICE, requestConsumerKeyFrame is wasted and
    // onRecvTransportReady will fire the burst.
    if (recvMediaReady) {
      void requestKeyframesForConsumer(consumer).catch(() => {});
    }

    restoreScheduler.scheduleSlotRestore(slot);
    // Already at its just-attached/just-reset baseline in every reachable case (a
    // replaced consumer's stats were just zeroed via disposePreviousConsumer above; a
    // never-before-attached slot starts zeroed from pushSlotDefaults) — resetSlot here
    // is what makes that explicit rather than relying on that invariant silently holding.
    rtpStats.resetSlot(slot);
    latencyControl.receiverChanged(slot);
    void rtpStats.pollSlotRtpStats(slot);
  }

  /** Enable/disable rendering for a slot (gates keyframe requests; playback is the host's job). */
  function setSlotEnabled(slot: number, enabled: boolean) {
    ensureSlots(slot);
    if (slot >= 0 && slot < slotCount) slotStreamingEnabled[slot] = Boolean(enabled);
    if (enabled) restoreScheduler.scheduleSlotRestore(slot);
  }

  async function resumeAllConsumers(): Promise<number> {
    let count = 0;
    for (let slot = 0; slot < slotCount; slot += 1) {
      if (restoreSlot(slot)) count += 1;
    }
    return count;
  }

  /** Total number of slots currently holding a video track (no-consumer probe). */
  function countVideoTracks(): number {
    let n = 0;
    for (let i = 0; i < slotCount; i += 1) {
      if ((streams[i]?.getVideoTracks?.().length ?? 0) > 0) n += 1;
    }
    return n;
  }

  return {
    ensureSlots,
    getSlotCount: () => slotCount,
    getSlotStream,
    onSlotStream,
    onSlotMetrics: rtpStats.onSlotMetrics,
    setLatencyMode: latencyControl.setMode,
    startLatencyComparison: latencyControl.startComparison,
    cancelLatencyComparison: latencyControl.cancelComparison,
    requestKeyframes,
    restoreSlot,
    setSlotEnabled,
    setHoldLastFrame,
    scheduleReleaseHoldLastFrame,
    dropConsumerById,
    attachVideoConsumer,
    onRecvTransportReady,
    getAggregateConsumerStats: rtpStats.getAggregateConsumerStats,
    resumeAllConsumers,
    countVideoTracks,
    destroy,
    startStatsPolling: rtpStats.startStatsPolling,
  };
}

export type DualVideoPipeline = ReturnType<typeof createDualVideoPipeline>;
export type { SlotMetrics };
